import { useState, useEffect } from 'react'
import axios from '../utils/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Textarea } from '../components/ui/textarea'
import { Plus, Edit, Trash2, Package } from 'lucide-react'

export default function ManageStore() {
  const [gifts, setGifts] = useState([])
  const [sponsors, setSponsors] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingGift, setEditingGift] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pointsCost: '',
    stockQuantity: '',
    imageUrl: '',
    sponsorId: ''
  })

  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      try {
        setLoading(true)
        const auth = JSON.parse(localStorage.getItem('auth'))
        const token = auth?.token
        if (!token) {
          window.location.href = '/admin/login'
          return
        }
        const [giftsRes, sponsorsRes] = await Promise.all([
          axios.get('/admin/gifts', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/admin/sponsors', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ])
        
        if (cancelled) return
        
        setGifts(giftsRes.data.gifts || [])
        const sponsorData = Array.isArray(sponsorsRes.data) ? sponsorsRes.data : (sponsorsRes.data.sponsors || [])
        setSponsors(sponsorData)
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching data:', error)
        if (error.response?.status === 401) {
          window.location.href = '/admin/login'
        }
        alert('Failed to fetch data')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [])

  const fetchGifts = async () => {
    try {
      setLoading(true)
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const response = await axios.get('/admin/gifts', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setGifts(response.data.gifts || [])
    } catch (error) {
      console.error('Error fetching gifts:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert('Failed to fetch gifts')
    } finally {
      setLoading(false)
    }
  }

  const fetchSponsors = async () => {
    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) return
      
      const response = await axios.get('/admin/sponsors', {
        headers: { Authorization: `Bearer ${token}` }
      })
      const sponsorData = Array.isArray(response.data) ? response.data : (response.data.sponsors || [])
      setSponsors(sponsorData)
    } catch (error) {
      console.error('Error fetching sponsors:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.name || !formData.pointsCost || !formData.stockQuantity) {
      alert('Please fill in all required fields')
      return
    }

    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      const data = {
        name: formData.name,
        description: formData.description,
        pointsCost: parseInt(formData.pointsCost),
        stockQuantity: parseInt(formData.stockQuantity),
        imageUrl: formData.imageUrl,
        sponsorId: formData.sponsorId || null
      }

      if (editingGift) {
        await axios.put(
          `/admin/gifts/${editingGift.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        alert('Gift updated successfully!')
      } else {
        await axios.post(
          '/admin/gifts',
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        alert('Gift added successfully!')
      }

      resetForm()
      fetchGifts()
    } catch (error) {
      console.error('Error saving gift:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to save gift')
    }
  }

  const handleEdit = (gift) => {
    setEditingGift(gift)
    setFormData({
      name: gift.name,
      description: gift.description || '',
      pointsCost: gift.pointsCost.toString(),
      stockQuantity: gift.stockQuantity.toString(),
      imageUrl: gift.imageUrl || '',
      sponsorId: gift.sponsorId?.toString() || ''
    })
    setShowForm(true)
  }

  const handleDelete = async (giftId) => {
    if (!window.confirm('Are you sure you want to delete this gift item?')) {
      return
    }

    try {
      const auth = JSON.parse(localStorage.getItem('auth'))
      const token = auth?.token
      if (!token) {
        window.location.href = '/admin/login'
        return
      }
      await axios.delete(`/admin/gifts/${giftId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      alert('Gift deleted successfully!')
      fetchGifts()
    } catch (error) {
      console.error('Error deleting gift:', error)
      if (error.response?.status === 401) {
        window.location.href = '/admin/login'
      }
      alert(error.response?.data?.error || 'Failed to delete gift')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      pointsCost: '',
      stockQuantity: '',
      imageUrl: '',
      sponsorId: ''
    })
    setEditingGift(null)
    setShowForm(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Manage Store</h1>
          <p className="text-muted-foreground mt-1">Add and manage gift items</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Gift Item
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingGift ? 'Edit' : 'Add'} Gift Item</CardTitle>
            <CardDescription>
              {editingGift ? 'Update' : 'Create a new'} gift item for the store
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Gift item name"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Image URL</label>
                  <input
                    type="url"
                    value={formData.imageUrl}
                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Sponsor</label>
                  <select
                    value={formData.sponsorId}
                    onChange={(e) => setFormData({ ...formData, sponsorId: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">No Sponsor</option>
                    {sponsors.map((sponsor) => (
                      <option key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium">Points Cost *</label>
                  <input
                    type="number"
                    value={formData.pointsCost}
                    onChange={(e) => setFormData({ ...formData, pointsCost: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="100"
                    min="1"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Stock Quantity *</label>
                  <input
                    type="number"
                    value={formData.stockQuantity}
                    onChange={(e) => setFormData({ ...formData, stockQuantity: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-input rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="50"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the gift item..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {editingGift ? 'Update' : 'Add'} Gift
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Gifts List */}
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">Loading gifts...</p>
          </CardContent>
        </Card>
      ) : gifts.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-center text-muted-foreground">No gift items found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {gifts.map((gift) => (
            <Card key={gift.id}>
              <CardContent className="p-4">
                <div className="space-y-4">
                  {gift.imageUrl && (
                    <img
                      src={gift.imageUrl}
                      alt={gift.name}
                      className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                  
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg">{gift.name}</h3>
                      <Badge variant={gift.stockQuantity > 0 ? 'default' : 'destructive'}>
                        {gift.stockQuantity > 0 ? 'In Stock' : 'Out of Stock'}
                      </Badge>
                    </div>
                    
                    {gift.sponsor && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Sponsored by: <span className="font-medium text-primary">{gift.sponsor.name}</span>
                      </div>
                    )}
                    
                    {gift.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {gift.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-4">
                      <div>
                        <div className="text-2xl font-bold text-primary">
                          {gift.pointsCost} pts
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          Stock: {gift.stockQuantity}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(gift)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(gift.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
